import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { UserProvider } from '../services/user.provider';

/**
 * redirects to the 404 page.
 * @returns {boolean} always false, so callers can `return redirectToUnauthorized(router)`.
 */
function redirectToUnauthorized(router: Router): boolean {
  router.navigate(['/404-page'], {
    replaceUrl: true,
  });
  return false;
}

/**
 * blocks access when the user lacks admin rights or the struttura-scoped permission required by the route.
 * @returns {boolean} true if the route can be activated, false otherwise.
 */
export const strutturaPermissionGuard: CanActivateFn = (route) => {
  const userProvider = inject(UserProvider);
  const router = inject(Router);

  const globalAdminPermission = route.data['permission'] as string;
  const strutturaPermission = route.data['strutturaPermission'] as string;

  if (!strutturaPermission) return redirectToUnauthorized(router);
  if (userProvider.isSuperAdmin || userProvider.isAdminUser) {
    return true;
  }
  if (
    globalAdminPermission &&
    userProvider.hasAdminPermission(globalAdminPermission)
  ) {
    return true;
  }

  const idStrutturaFromRoute = route.paramMap.get('id');
  const strutturaId = idStrutturaFromRoute
    ? Number(idStrutturaFromRoute)
    : null;

  if (strutturaId) {
    const userStrutture = userProvider.userStruttureList;
    const hasAccessToStruttura = userStrutture.some(
      (struttura) => struttura.id === strutturaId,
    );

    if (!hasAccessToStruttura) return redirectToUnauthorized(router);
  }

  const selectedStruttura = userProvider.currentStruttura;
  const permissionList = selectedStruttura?.permessiVisivi || [];
  const hasStrutturaPermission = permissionList.includes(strutturaPermission);

  if (hasStrutturaPermission) return true;

  return redirectToUnauthorized(router);
};
